import { useReducer, useRef } from 'react';
import { isDateValid, isDateAfter } from '../../components/common/util';

const SET_STARTDATE = 'startDate';
const SET_ENDDATE = 'endDate';
const SET_SCOPE = 'scope';
const SET_COMPLETE = 'complete';
const RESET = 'reset';

function canChange(prop, milestone) {
	return {
		startDate: !milestone.complete,
		endDate: !milestone.complete,
		scope: !milestone.complete,
		complete: Boolean(milestone.startDate && milestone.endDate)
	}[prop];
}

function cloneMilestone(state = {}) {
	return {
		...state,
		startDateLastChange: { ...state.startDateLastChange },
		endDateLastChange: { ...state.endDateLastChange },
		scopeLastChange: { ...state.scopeLastChange },
		completeLastChange: { ...state.completeLastChange }
	};
}

function replaceMilestone(state = [], milestone = {}) {
	return state.map(s => s.id === milestone.id ? milestone : s);
}

function getMilestoneStartDate(start) {
	return isDateValid(start) ? start : null;
}

function getMilestoneEndDate(start, end) {
	if (!isDateValid(end)) return null;
	if (isDateValid(start) && !isDateAfter(end, start)) return null;
	return end;
}

function getMilestoneScope(scope, complete) {
	return Boolean(complete || scope);
}

function getMilestoneComplete(complete, startDate, endDate) {
	return Boolean(startDate && endDate && complete);
}

function getMilestoneChange(change, history = []) {
	return {
		...history.find(h => h.key === change)
	};
}

function reduce(currentState = [], { type, payload }) {
	const newState = currentState.map(cloneMilestone);

	switch (type) {
		case SET_STARTDATE: {
			const { id, value } = payload;
			const ms = newState.find(s => s.id === id);
			const isDate = isDateValid(value);

			ms.startDate = isDate && canChange('startDate', ms) ? value : ms.startDate;
			ms.completeDisabled = !canChange('complete', ms);

			return replaceMilestone(newState, ms);
		}
		case SET_ENDDATE: {
			const { id, value } = payload;
			const ms = newState.find(s => s.id === id);
			const isDate = isDateValid(value);

			ms.endDate = isDate && canChange('endDate', ms) ? value : ms.endDate;
			ms.completeDisabled = !canChange('complete', ms);

			return replaceMilestone(newState, ms);
		}
		case SET_SCOPE: {
			const { id, value } = payload;
			const unscoping = value !== true;
			const othersInScope = newState.filter(s => s.id !== id && s.scope);

			if (unscoping) {
				// must have at least one milestone in scope
				if (othersInScope.length === 0) return newState;
				if (othersInScope.length === 1) {
					othersInScope[0].scopeDisabled = true;
				}
			} else {
				// at least two are present now so others can be enabled
				newState.forEach(m => {
					if (!m.complete) m.scopeDisabled = false;
				});
			}

			const ms = newState.find(s => s.id === id);

			ms.scope = canChange('scope', ms) ? Boolean(value) : true;
			ms.startDateDisabled = ms.complete || !ms.scope;
			ms.endDateDisabled = ms.complete || !ms.scope;
			ms.completeDisabled = !ms.scope || !canChange('complete', ms);

			if (!ms.scope) {
				ms.startDate = null;
				ms.endDate = null;
			}

			return replaceMilestone(newState, ms);
		}
		case SET_COMPLETE: {
			const { id, value } = payload;
			const ms = newState.find(s => s.id === id);

			ms.complete = canChange('complete', ms) && Boolean(value);
			ms.startDateDisabled = ms.complete;
			ms.endDateDisabled = ms.complete;
			ms.scopeDisabled = ms.complete;

			if (ms.complete) {
				ms.scope = true;
			} else {
				const othersInScope = newState.some(s => s.scope && ms.id !== s.id);
				if (!othersInScope) {
					// disable unscoping if there's no other milestone in scope
					ms.scopeDisabled = true;
				}
			}

			return replaceMilestone(newState, ms);
		}
		case RESET: {
			const { milestones, hasProductionProject } = payload || {};
			return getInitialState(milestones, hasProductionProject);
		}
		default:
			return currentState;
	}
}

function getInitialState(milestones = [], hasProductionProject) {
	return milestones.map(ms => {
		const { id, name, description, duration, startDate, endDate, scope, complete, history = [] } = ms || {};
		const start = getMilestoneStartDate(startDate, endDate);
		const end = getMilestoneEndDate(start, endDate);
		const inScope = getMilestoneScope(scope, complete);
		const isComplete = getMilestoneComplete(complete, startDate, endDate);
		return {
			id,
			name: String(name),
			description,
			duration,
			startDate: start,
			endDate: end,
			scope: inScope,
			complete: isComplete,
			startDateDisabled: Boolean(hasProductionProject) || !inScope || isComplete,
			endDateDisabled: Boolean(hasProductionProject) || !inScope || isComplete,
			scopeDisabled: Boolean(hasProductionProject) || isComplete || !milestones.find(s => s.id !== id && s.scope),
			completeDisabled: Boolean(hasProductionProject) || !start || !end,
			startDateLastChange: getMilestoneChange('STARTDATE', history),
			endDateLastChange: getMilestoneChange('ENDDATE', history),
			scopeLastChange: getMilestoneChange('SCOPE', history),
			completeLastChange: getMilestoneChange('COMPLETE', history)
		};
	});
}

export default function useMilestones(milestones, hasProductionProject) {
	const [state, dispatch] = useReducer(reduce, milestones, s => getInitialState(s, hasProductionProject));
	const onChange = useRef(null);

	const createAction = (dispatch, type) => payload => {
		dispatch({ type, payload });

		if (type !== RESET && typeof onChange.current === 'function') {
			onChange.current({
				id: payload.id,
				name: type,
				value: payload.value
			});
		}
	};

	return {
		milestones: state,
		actions: {
			setOnChange: fn => onChange.current = fn,
			setStartDate: createAction(dispatch, SET_STARTDATE),
			setEndDate: createAction(dispatch, SET_ENDDATE),
			setScope: createAction(dispatch, SET_SCOPE),
			setComplete: createAction(dispatch, SET_COMPLETE),
			reset: createAction(dispatch, RESET),
		}
	};
};